// @ts-nocheck
import { resolveColor, safeColor } from "@insite/mobius/utilities";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
// import { buttonStyles } from "@store/Themes/common/mixins/button"; // Store Custom Code
import cssProp from "@store/Framework/Common/Utilities/cssProp";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { ButtonType, MobiusButtonType } from "@toolkit/Framework/Types/Button";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
import { buttonGetHoverProps } from "@store/Themes/common/props/button";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { buttonStyles } from "@store/Themes/common/mixins/button";
/* Store Custom Code - End */

/* ------------------------------------------------------------------------------------------ */
/* Related Extensions => src/Extensions/Mobius/Button --------------------------------------- */
/* ------------------------------------------------------------------------------------------ */

interface ButtonOptions {
    color: string;
    type: ButtonType;
    hoverColor?: string;
    hoverTextColor?: string;
    // Store Custom Code
    textColor?: string;
    borderColor?: string;
    hoverBorderColor?: string;
}

const getButton = (options: ButtonOptions): ButtonPresentationProps => ({
    shape: "rounded",
    buttonType: options.type as MobiusButtonType,
    typographyProps: { weight: "inherit" },
    color: options.color,
    /* Store Custom Code - Start */
    css: css`
        ${buttonStyles(options.type, options.textColor)}

        ${({ theme }) => {
            return css`
                ${cssProp("border-color", options.borderColor, x => resolveColor(x, theme))}

                &:hover {
                    ${cssProp("border-color", options.hoverBorderColor, x => resolveColor(x, theme))}
                }
            `;
        }};
    `,
    /* Store Custom Code - End */
    ...buttonGetHoverProps(options.hoverColor || `${options.color}.dark`),
    hoverTextColor: options.hoverTextColor,
});

interface SelectionButtonOptions {
    color: string;
    hoverColor: string;
    textColor?: string;
    showBackground?: boolean;
}

const getSelectionButton = (options: SelectionButtonOptions = { color: "", hoverColor: "", showBackground: true }) =>
    mergeProps<ButtonPresentationProps>(
        getButton({ color: options.color, type: "outline", hoverColor: options.hoverColor }),
        {
            css: css`
                max-width: 100%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;

                ${({ theme }) => {
                    return css`
                        ${cssProp("color", options.textColor, x => resolveColor(x, theme))}
                        ${options.showBackground
                            ? `background-color: ${safeColor(resolveColor(options.color, theme)).alpha(0.1)};`
                            : null}
                    font-weight: ${theme.design.typography.weight.base};

                        &:disabled {
                            background-color: ${safeColor(theme.colors.button.disabled.contrast).alpha(0.1).toString()};
                        }
                    `;
                }};
            `,
        },
    );

const button: Styleguide["button"] = {
    primary: getButton({ color: "primary", type: "solid" }),
    /* Store Custom Code - Start */
    secondary: getButton({
        color: "common.border",
        textColor: "secondary.contrast",
        hoverColor: "common.borderDark",
        type: "solid",
    }),
    tertiary: getButton({ color: "primary", hoverTextColor: "common.accentContrast", type: "outline" }),
    inverted: getButton({ color: "invert", hoverTextColor: "invert.hover", type: "solid" }),
    invertedOutline: getButton({
        color: "invert",
        textColor: "invert.contrast",
        hoverTextColor: "invert.hover",
        type: "solid",
        borderColor: "invert.contrast",
        hoverBorderColor: "invert.hover",
    }),
    /* Store Custom Code - End */
    selected: getSelectionButton({ color: "swatch.selected", hoverColor: "swatch.selectedHover" }),
    unselected: getSelectionButton({
        color: "swatch",
        hoverColor: "swatch.hover",
        textColor: "swatch.contrast",
        showBackground: false,
    }),
};

export default button;
